import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Save, ArrowLeft, Upload, FileText, Download } from "lucide-react";

const emptyForm = {
  customer_name: "",
  customer_po: "",
  customer_contact: "",
  customer_phone: "",
  customer_email: "",
  customer_address: "",
  customer_gst: "",
  artwork_version: "",
  item_details: { item_name: "", item_type: "", thickness: "", qty: 0, print_type: "", remarks: "" },
  items: [],
  product_description: "",
  ordered_qty: 0,
  due_date: "",
  materials: [],
  production_logs: [],
  rejection_qty: 0,
  wastage_qty: 0,
  good_qty: 0,
  production_rejection_qty: 0,
  production_wastage_qty: 0,
  production_good_qty: 0,
  operator_name: "",
  checklist: [],
  artwork_files: [],
  notes: "",
  status: "draft",
};

const QUALITY_OPTIONS = ["Good", "Bad", "Other"];

// Front/Back are picked by explicit slot tag, never by array position, so
// deleting one image never shifts the other into its place. Falls back to
// order for items saved before slot tagging existed.
const pickFrontBack = (files) => {
  const arr = files || [];
  let front = arr.find((f) => f.slot === "front");
  let back = arr.find((f) => f.slot === "back");
  let others;
  if (front || back) {
    others = arr.filter((f) => f !== front && f !== back);
  } else {
    front = arr[0] || null;
    back = arr[1] || null;
    others = arr.slice(2);
  }
  return { front, back, others };
};

const emptyItem = () => ({
  item_name: "", item_type: "", thickness: "", qty: 0, print_type: "", remarks: "",
  card_size: "", finish: "", embossing: "", type_of_card: "",
  tag_card_no: "", qr_link: "", chip_encoding: "", variable_data: "",
  artwork_version: "",
  artwork_files: [],
});

export default function JobCardForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [customers, setCustomers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [checklistMaster, setChecklistMaster] = useState([]);
  const [artworkMaster, setArtworkMaster] = useState([]);
  const [materialsMaster, setMaterialsMaster] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showSaveCustomer, setShowSaveCustomer] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      const [cust, m, cl, aw, mat] = await Promise.all([
        api.get("/customers"),
        api.get("/machines"),
        api.get("/checklist-master"),
        api.get("/artwork-master"),
        api.get("/materials-master"),
      ]);
      const toArr = (d) => Array.isArray(d) ? d : [];
      setCustomers(toArr(cust.data));
      setMachines(toArr(m.data));
      setChecklistMaster(toArr(cl.data));
      setArtworkMaster(toArr(aw.data));
      setMaterialsMaster(toArr(mat.data));
      if (isEdit) {
        const { data } = await api.get(`/job-cards/${id}`);
        // Migrate legacy single item_details + top-level artwork_files into items[0] if items is empty
        let items = data.items || [];
        if (items.length === 0 && (data.item_details?.item_name || (data.artwork_files || []).length > 0)) {
          items = [{
            ...emptyItem(),
            ...(data.item_details || {}),
            artwork_files: data.artwork_files || [],
          }];
        }
        setForm({
          ...emptyForm,
          ...data,
          item_details: { ...emptyForm.item_details, ...(data.item_details || {}) },
          items,
          checklist: data.checklist || data.packing_checklist || [],
          artwork_files: data.artwork_files || [],
        });
      } else {
        // Prefill new form with master checklist and one empty item
        setForm((f) => ({
          ...f,
          items: [emptyItem()],
          checklist: (Array.isArray(cl.data) ? cl.data : []).map((c) => ({ label: c.label, checked: false })),
        }));
      }
    })();
  }, [id, isEdit]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setItem = (k, v) => setForm((s) => ({ ...s, item_details: { ...s.item_details, [k]: v } }));

  // Multi-item helpers
  const addItem = () => set("items", [...(form.items || []), emptyItem()]);
  const updItem = (i, k, v) =>
    set("items", form.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const updItemBatch = (i, patch) =>
    setForm((s) => ({ ...s, items: s.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  const rmItem = (i) => set("items", form.items.filter((_, idx) => idx !== i));

  const uploadForItem = async (i, files) => {
    const fileList = Array.isArray(files) ? files : [files];
    const uploaded = [];
    for (const file of fileList) {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/artwork", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      uploaded.push(data);
    }
    const it = form.items[i];
    updItem(i, "artwork_files", [...(it.artwork_files || []), ...uploaded]);
    if (uploaded.length === 1) toast.success(`Uploaded ${uploaded[0].original_filename}`);
    else toast.success(`Uploaded ${uploaded.length} files`);
  };

  const rmItemFile = (i, fid) => {
    const it = form.items[i];
    // Normalize Front/Back into explicit slot tags before removing, so
    // deleting one never shifts the other into its display position.
    const { front, back, others } = pickFrontBack(it.artwork_files);
    const normalized = [
      front ? { ...front, slot: "front" } : null,
      back ? { ...back, slot: "back" } : null,
      ...others,
    ].filter(Boolean);
    updItem(i, "artwork_files", normalized.filter((f) => f.id !== fid));
  };

  const onCustomerSelect = (customerId) => {
    if (customerId === "__new__") {
      setShowSaveCustomer(true);
      return;
    }
    const c = customers.find((x) => x.id === customerId);
    if (c) {
      setForm((f) => ({
        ...f,
        customer_name: c.name,
        customer_po: f.customer_po,
        customer_contact: c.contact_person || "",
        customer_email: c.email || "",
        customer_phone: c.phone || "",
        customer_address: c.address || "",
        customer_gst: c.gst_no || "",
      }));
    }
  };

  const saveNewCustomer = async () => {
    if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
    try {
      const { data } = await api.post("/customers", {
        name: form.customer_name.trim(),
        contact_person: form.customer_contact || "",
        email: form.customer_email || "",
        phone: form.customer_phone || "",
        address: form.customer_address || "",
        gst_no: form.customer_gst || "",
      });
      setCustomers((prev) => [...prev, data]);
      setShowSaveCustomer(false);
      toast.success(`Customer "${data.name}" saved to master`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save customer");
    }
  };

  const addMaterial = () =>
    set("materials", [...form.materials, { material_name: "", unit: "", issued_qty: 0, consumed_qty: 0, remarks: "" }]);
  const updMaterial = (i, k, v) =>
    set("materials", form.materials.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
  const rmMaterial = (i) => set("materials", form.materials.filter((_, idx) => idx !== i));

  const addLog = () =>
    set("production_logs", [...form.production_logs, { machine: "", operator: "", start_time: "", end_time: "", output_qty: 0, remarks: "" }]);
  const updLog = (i, k, v) =>
    set("production_logs", form.production_logs.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
  const rmLog = (i) => set("production_logs", form.production_logs.filter((_, idx) => idx !== i));

  const updCheck = (i, checked) =>
    set("checklist", form.checklist.map((c, idx) => (idx === i ? { ...c, checked } : c)));
  const addChecklist = () => set("checklist", [...form.checklist, { label: "New item", checked: false }]);
  const updChecklistLabel = (i, v) =>
    set("checklist", form.checklist.map((c, idx) => (idx === i ? { ...c, label: v } : c)));
  const rmChecklist = (i) => set("checklist", form.checklist.filter((_, idx) => idx !== i));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/artwork", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("artwork_files", [...(form.artwork_files || []), data]);
      toast.success(`Uploaded ${data.original_filename}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const rmFile = (fid) =>
    set("artwork_files", (form.artwork_files || []).filter((f) => f.id !== fid));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        ordered_qty: Number(form.ordered_qty) || 0,
        rejection_qty: Number(form.rejection_qty) || 0,
        wastage_qty: Number(form.wastage_qty) || 0,
        good_qty: Number(form.good_qty) || 0,
        production_rejection_qty: Number(form.production_rejection_qty) || 0,
        production_wastage_qty: Number(form.production_wastage_qty) || 0,
        production_good_qty: Number(form.production_good_qty) || 0,
        item_details: { ...form.item_details, qty: Number(form.item_details.qty) || 0 },
        items: (form.items || []).map((it) => ({
          ...it,
          qty: Number(it.qty) || 0,
          artwork_files: it.artwork_files || [],
        })),
        materials: form.materials.map((m) => ({
          ...m, issued_qty: Number(m.issued_qty) || 0, consumed_qty: Number(m.consumed_qty) || 0,
        })),
        production_logs: form.production_logs.map((p) => ({
          ...p, output_qty: Number(p.output_qty) || 0,
        })),
      };
      const res = isEdit
        ? await api.put(`/job-cards/${id}`, payload)
        : await api.post("/job-cards", payload);

      toast.success(isEdit ? "Job card updated" : `Created ${res.data.job_card_no}`);
      navigate(`/job-cards/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>
          {isEdit ? "Edit Job Card" : "New Job Card"}
        </h1>
      </div>

      <form onSubmit={onSubmit} data-testid="jc-form" className="pb-24">
        <Section title="Customer & PO" hint="Section 01">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Select from saved customers" full className="md:col-span-2">
              <Select onValueChange={onCustomerSelect}>
                <SelectTrigger className="bg-white" data-testid="jc-customer-select">
                  <SelectValue placeholder="Pick from master list…" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                  <SelectItem value="__new__">＋ Save current as new customer</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Customer Name" required>
              <Input maxLength={150} value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} required data-testid="jc-customer" />
            </Field>
            <Field label="Customer PO No." required>
              <Input maxLength={150} value={form.customer_po} onChange={(e) => set("customer_po", e.target.value)} required data-testid="jc-po" />
            </Field>
            <Field label="Contact Person">
              <Input maxLength={150} value={form.customer_contact || ""} onChange={(e) => set("customer_contact", e.target.value)} placeholder="Contact name" />
            </Field>
            <Field label="Phone">
              <Input maxLength={150} value={form.customer_phone || ""} onChange={(e) => set("customer_phone", e.target.value)} placeholder="Phone number" />
            </Field>
            <Field label="Email">
              <Input maxLength={150} value={form.customer_email || ""} onChange={(e) => set("customer_email", e.target.value)} placeholder="Email address" />
            </Field>
            <Field label="GST No.">
              <Input maxLength={150} value={form.customer_gst || ""} onChange={(e) => set("customer_gst", e.target.value)} placeholder="GST number" />
            </Field>
            <Field label="Address" full className="md:col-span-2">
              <Input maxLength={150} value={form.customer_address || ""} onChange={(e) => set("customer_address", e.target.value)} placeholder="Address" />
            </Field>

            {showSaveCustomer && (
              <div className="md:col-span-2 flex items-center gap-3 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3">
                <span className="text-sm text-yellow-900 flex-1">Save <b>{form.customer_name || "this customer"}</b> to the customer master for future use?</span>
                <Button type="button" size="sm" className="bg-yellow-400 hover:bg-yellow-500 text-slate-900" onClick={saveNewCustomer}>Save Customer</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setShowSaveCustomer(false)}>Cancel</Button>
              </div>
            )}

            <Field label="Order Quality">
              <Select value={form.artwork_version || undefined} onValueChange={(v) => set("artwork_version", v)}>
                <SelectTrigger className="bg-white" data-testid="jc-artwork"><SelectValue placeholder="Select quality…" /></SelectTrigger>
                <SelectContent>
                  {QUALITY_OPTIONS.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Due Date">
              <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="jc-due" />
            </Field>
            <Field label="Product Description" full className="md:col-span-2">
              <Textarea maxLength={500} value={form.product_description} onChange={(e) => set("product_description", e.target.value)} rows={2} data-testid="jc-product" />
            </Field>
          </div>
        </Section>

        {/* Section 02 - Item Details */}
        <Section
          title="Items & Artwork"
          hint="Section 02"
          actions={
            <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item">
              <Plus className="w-4 h-4 mr-1" /> Add item
            </Button>
          }
        >
          <div className="mb-4">
            <Field label="Ordered Qty (total)">
              <Input type="number" min="0" value={form.ordered_qty === 0 ? "" : form.ordered_qty} onChange={(e) => set("ordered_qty", Math.max(0, Number(e.target.value) || 0))} onFocus={(e) => { if (Number(e.target.value) === 0) e.target.value = ""; }} className="font-mono max-w-xs" data-testid="jc-ordered" />
            </Field>
          </div>

          {form.items.length === 0 && (
            <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded p-6 text-center">
              No items added yet. Click <b>Add item</b> to include specifications and artwork.
            </div>
          )}

          <div className="space-y-4">
            {form.items.map((it, i) => (
              <ItemCard
                key={i}
                index={i}
                item={it}
                artworkMaster={artworkMaster}
                onArtworkMasterSaved={(a) => setArtworkMaster((prev) => [...prev, a])}
                onChange={(k, v) => updItem(i, k, v)}
                onBatchChange={(patch) => updItemBatch(i, patch)}
                onRemove={() => rmItem(i)}
                onUpload={(file) => uploadForItem(i, file)}
                onRemoveFile={(fid) => rmItemFile(i, fid)}
              />
            ))}
          </div>
        </Section>

        <Section
          title="Material Issue & Consumption"
          hint="Section 03"
          actions={<Button type="button" variant="outline" size="sm" onClick={addMaterial} data-testid="add-material"><Plus className="w-4 h-4 mr-1" /> Add row</Button>}
        >
          {form.materials.length === 0 && (
            <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded p-6 text-center">
              No materials added yet. Click <b>Add row</b> to add material consumption.
            </div>
          )}
          <div className="space-y-3">
            {form.materials.map((m, i) => (
              <MaterialRow
                key={i}
                index={i}
                material={m}
                materialsMaster={materialsMaster}
                onMaterialMasterSaved={(saved) => setMaterialsMaster((prev) => [...prev, saved])}
                onChange={(k, v) => updMaterial(i, k, v)}
                onBatchChange={(patch) => set("materials", form.materials.map((x, idx) => idx === i ? { ...x, ...patch } : x))}
                onRemove={() => rmMaterial(i)}
              />
            ))}
          </div>
        </Section>

        <Section
          title="Machine-wise Production Log"
          hint="Section 04"
          actions={<Button type="button" variant="outline" size="sm" onClick={addLog} data-testid="add-log"><Plus className="w-4 h-4 mr-1" /> Add log</Button>}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Machine</th><th className="py-2 pr-3">Operator</th>
                  <th className="py-2 pr-3">Start</th><th className="py-2 pr-3">End</th>
                  <th className="py-2 pr-3 text-right">Output</th><th className="py-2 pr-3">Remarks</th><th></th>
                </tr>
              </thead>
              <tbody>
                {form.production_logs.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-500">No production logs</td></tr>}
                {form.production_logs.map((p, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 pr-3 w-44">
                      <Select value={p.machine} onValueChange={(v) => updLog(i, "machine", v)}>
                        <SelectTrigger className="bg-white" data-testid={`log-machine-${i}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{machines.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 pr-3"><Input maxLength={150} value={p.operator} onChange={(e) => updLog(i, "operator", e.target.value)} data-testid={`log-op-${i}`} /></td>
                    <td className="py-2 pr-3 w-44"><Input type="datetime-local" value={p.start_time} onChange={(e) => updLog(i, "start_time", e.target.value)} data-testid={`log-start-${i}`} /></td>
                    <td className="py-2 pr-3 w-44"><Input type="datetime-local" value={p.end_time} onChange={(e) => updLog(i, "end_time", e.target.value)} data-testid={`log-end-${i}`} /></td>
                    <td className="py-2 pr-3 w-24"><Input type="number" min="0" value={p.output_qty === 0 ? "" : p.output_qty} onChange={(e) => updLog(i, "output_qty", Math.max(0, Number(e.target.value) || 0))} onFocus={(e) => { if (Number(e.target.value) === 0) e.target.value = ""; }} className="text-right font-mono" data-testid={`log-out-${i}`} /></td>
                    <td className="py-2 pr-3"><Input maxLength={150} value={p.remarks} onChange={(e) => updLog(i, "remarks", e.target.value)} /></td>
                    <td className="py-2 w-10"><Button type="button" variant="ghost" size="icon" onClick={() => rmLog(i)} data-testid={`log-rm-${i}`}><Trash2 className="w-4 h-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="Rejection, Wastage & Reconciliation" hint="Section 05">
          <div className="space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Printing Reconciliation</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Good Qty"><Input type="number" min="0" value={form.good_qty === 0 ? "" : form.good_qty} onChange={(e) => set("good_qty", Math.max(0, Number(e.target.value) || 0))} onFocus={(e) => { if (Number(e.target.value) === 0) e.target.value = ""; }} className="text-right font-mono" data-testid="jc-good" /></Field>
                <Field label="Rejected Qty"><Input type="number" min="0" value={form.rejection_qty === 0 ? "" : form.rejection_qty} onChange={(e) => set("rejection_qty", Math.max(0, Number(e.target.value) || 0))} onFocus={(e) => { if (Number(e.target.value) === 0) e.target.value = ""; }} className="text-right font-mono" data-testid="jc-reject" /></Field>
                <Field label="Wasted Qty"><Input type="number" min="0" value={form.wastage_qty === 0 ? "" : form.wastage_qty} onChange={(e) => set("wastage_qty", Math.max(0, Number(e.target.value) || 0))} onFocus={(e) => { if (Number(e.target.value) === 0) e.target.value = ""; }} className="text-right font-mono" data-testid="jc-wastage" /></Field>
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Production Reconciliation</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Good Qty"><Input type="number" min="0" value={form.production_good_qty === 0 ? "" : form.production_good_qty} onChange={(e) => set("production_good_qty", Math.max(0, Number(e.target.value) || 0))} onFocus={(e) => { if (Number(e.target.value) === 0) e.target.value = ""; }} className="text-right font-mono" data-testid="jc-prod-good" /></Field>
                <Field label="Rejected Qty"><Input type="number" min="0" value={form.production_rejection_qty === 0 ? "" : form.production_rejection_qty} onChange={(e) => set("production_rejection_qty", Math.max(0, Number(e.target.value) || 0))} onFocus={(e) => { if (Number(e.target.value) === 0) e.target.value = ""; }} className="text-right font-mono" data-testid="jc-prod-reject" /></Field>
                <Field label="Wasted Qty"><Input type="number" min="0" value={form.production_wastage_qty === 0 ? "" : form.production_wastage_qty} onChange={(e) => set("production_wastage_qty", Math.max(0, Number(e.target.value) || 0))} onFocus={(e) => { if (Number(e.target.value) === 0) e.target.value = ""; }} className="text-right font-mono" data-testid="jc-prod-wastage" /></Field>
              </div>
            </div>
            <Field label="Operator Name" className="max-w-sm"><Input maxLength={150} value={form.operator_name} onChange={(e) => set("operator_name", e.target.value)} data-testid="jc-operator" /></Field>
          </div>
          <ReconciliationSummary form={form} />
        </Section>

        {/* Section 06 - Checklist */}
        <Section
          title="Checklist"
          hint="Section 06"
          actions={<Button type="button" variant="outline" size="sm" onClick={addChecklist} data-testid="add-check"><Plus className="w-4 h-4 mr-1" /> Add item</Button>}
        >
          {form.checklist.length === 0 && (
            <div className="text-sm text-slate-500 mb-2">
              No checklist items. Add default items in Admin → Checklist to auto-populate future job cards.
            </div>
          )}
          <div className="space-y-2">
            {form.checklist.map((c, i) => (
              <div key={i} className="flex items-center gap-3 border border-slate-200 rounded-md p-3 bg-white">
                <Checkbox checked={c.checked} onCheckedChange={(v) => updCheck(i, Boolean(v))} data-testid={`check-${i}`} />
                <Input maxLength={150} value={c.label} onChange={(e) => updChecklistLabel(i, e.target.value)} className="border-0 focus-visible:ring-0 shadow-none" data-testid={`check-label-${i}`} />
                <Button type="button" variant="ghost" size="icon" onClick={() => rmChecklist(i)} data-testid={`check-rm-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Notes & Status" hint="Section 07">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="bg-white" data-testid="jc-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Notes" full className="md:col-span-2">
              <Textarea maxLength={500} value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="jc-notes" />
            </Field>
          </div>
        </Section>

        <div className="fixed bottom-0 left-[240px] right-0 z-20 bg-white/95 backdrop-blur-sm border-t border-slate-200 px-6 lg:px-10 py-3 flex items-center justify-end gap-3">
          <Button type="button" variant="outline" size="sm" className="min-w-[90px]" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" size="sm" className="min-w-[140px] bg-slate-900 hover:bg-slate-800 text-white" disabled={saving} data-testid="jc-submit">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : (isEdit ? "Update Job Card" : "Create Job Card")}
          </Button>
        </div>
      </form>
    </Layout>
  );
}

function ItemCard({ index, item, artworkMaster = [], onArtworkMasterSaved, onChange, onBatchChange, onRemove, onUpload, onRemoveFile }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(null); // null | "front" | "back" | "other"
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const [savingMaster, setSavingMaster] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);
  const [pendingReset, setPendingReset] = useState(false);

  useEffect(() => {
    if (pendingReset) {
      setPickerKey((k) => k + 1);
      setPendingReset(false);
    }
  }, [item, pendingReset]);

  const isAlreadyInMaster = artworkMaster.some(
    (a) => a.name.trim().toLowerCase() === (item.item_name || "").trim().toLowerCase()
  );

  const saveToMaster = async () => {
    if (!item.item_name.trim()) { toast.error("Enter an item name first"); return; }
    setSavingMaster(true);
    try {
      const lastFile = (item.artwork_files || []).slice(-1)[0];
      const { data } = await api.post("/artwork-master/upsert", {
        name: item.item_name.trim(),
        item_type: item.item_type || "",
        thickness: item.thickness || "",
        print_type: item.print_type || "",
        description: item.remarks || "",
        customer_name: "",
        file_id: lastFile?.id || null,
        storage_path: lastFile?.storage_path || "",
        original_filename: lastFile?.original_filename || "",
        size: lastFile?.size || 0,
        content_type: lastFile?.content_type || "",
      });
      onArtworkMasterSaved(data);
      toast.success(`"${item.item_name}" saved to Products`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingMaster(false);
    }
  };

  const handleFile = async (e, slot) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(slot || "other");
    try {
      if (slot === "front" || slot === "back") {
        // Replace just the uploaded slot, tagging both slots explicitly so
        // the other image never shifts position (migrates legacy position-
        // based records to explicit tags in the same step).
        const file = files[0];
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload/artwork", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const { front, back, others } = pickFrontBack(item.artwork_files);
        const newFront = slot === "front" ? { ...data, slot: "front" } : (front ? { ...front, slot: "front" } : null);
        const newBack  = slot === "back"  ? { ...data, slot: "back" }  : (back  ? { ...back,  slot: "back"  } : null);
        onBatchChange({ artwork_files: [newFront, newBack, ...others].filter(Boolean) });
        toast.success(`${slot === "front" ? "Front" : "Back"} image uploaded`);
      } else {
        await onUpload(files);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
      if (e.target) e.target.value = "";
    }
  };

  const pickFromMaster = (artworkId) => {
    if (artworkId === "__none__") return;
    const a = artworkMaster.find((x) => x.id === artworkId);
    if (!a) return;
    // Batch all field updates into one state write so nothing gets lost
    const newFiles = [...(item.artwork_files || [])];
    if (a.file_id && !newFiles.find((f) => f.id === a.file_id)) {
      newFiles.push({
        id: a.file_id,
        storage_path: a.storage_path,
        original_filename: a.original_filename,
        size: a.size,
        content_type: a.content_type,
      });
    }
    onBatchChange({
      item_name: a.name,
      item_type: a.item_type || item.item_type,
      thickness: a.thickness || item.thickness,
      print_type: a.print_type || item.print_type,
      remarks: a.description || item.remarks,
      artwork_files: newFiles,
    });
    setPendingReset(true);
    toast.success(`Loaded "${a.name}" from Products master`);
  };

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50" data-testid={`item-card-${index}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-slate-900 text-white grid place-items-center text-[10px] font-mono">#{index + 1}</div>
          <div className="text-sm font-medium text-slate-800">Item {index + 1}</div>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onRemove} data-testid={`item-rm-${index}`}>
          <Trash2 className="w-4 h-4 mr-1" /> Remove
        </Button>
      </div>

      {/* Products master picker — at top like customer picker */}
      <div className="mb-3">
        <Field label="Pick from saved Products (auto-fills fields below)">
          <Select key={pickerKey} onValueChange={pickFromMaster}>
            <SelectTrigger className="bg-white" data-testid={`item-master-pick-${index}`}>
              <SelectValue placeholder={artworkMaster.length ? "Select a saved product to auto-fill…" : "No products saved yet"} />
            </SelectTrigger>
            <SelectContent>
              {artworkMaster.length === 0 && <SelectItem value="__none__" disabled>No products yet — fill below and save</SelectItem>}
              {artworkMaster.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name}{a.item_type ? ` · ${a.item_type}` : ""}{a.thickness ? ` · ${a.thickness}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      {/* Two-column layout matching the PDF approval sheet exactly */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* LEFT — JOB DETAILS */}
        <div className="border border-[#1d3d8f] rounded-lg overflow-hidden">
          <div className="bg-[#1d3d8f] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5">
            Job Details
          </div>
          <div className="p-3 space-y-2">
            <Field label="Item Name">
              <Input maxLength={150} value={item.item_name} onChange={(e) => onChange("item_name", e.target.value)} data-testid={`item-name-${index}`} />
            </Field>
            <Field label="Tag / Card Size">
              <Input maxLength={150} value={item.card_size} onChange={(e) => onChange("card_size", e.target.value)} data-testid={`item-card-size-${index}`} />
            </Field>
            <Field label="Embossing">
              <Input maxLength={150} value={item.embossing} onChange={(e) => onChange("embossing", e.target.value)} data-testid={`item-embossing-${index}`} />
            </Field>
            <Field label="Type of Card">
              <Input maxLength={150} value={item.type_of_card || item.item_type} onChange={(e) => { onChange("type_of_card", e.target.value); onChange("item_type", e.target.value); }} data-testid={`item-type-${index}`} />
            </Field>
            <Field label="Qty">
              <Input type="number" min="0" value={item.qty === 0 ? "" : item.qty} onChange={(e) => onChange("qty", Math.max(0, Number(e.target.value) || 0))} onFocus={(e) => { if (Number(e.target.value) === 0) e.target.value = ""; }} className="font-mono" data-testid={`item-qty-${index}`} />
            </Field>
            <Field label="Remarks / Notes">
              <Input maxLength={150} value={item.remarks} onChange={(e) => onChange("remarks", e.target.value)} data-testid={`item-remarks-${index}`} />
            </Field>
          </div>
        </div>

        {/* RIGHT — PRINTING AND TECHNICAL DETAILS */}
        <div className="border border-[#1d3d8f] rounded-lg overflow-hidden">
          <div className="bg-[#1d3d8f] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5">
            Printing and Technical Details
          </div>
          <div className="p-3 space-y-2">
            <Field label="Printing">
              <Input maxLength={150} value={item.print_type} onChange={(e) => onChange("print_type", e.target.value)} data-testid={`item-print-type-${index}`} />
            </Field>
            <Field label="Finish">
              <Input maxLength={150} value={item.finish} onChange={(e) => onChange("finish", e.target.value)} data-testid={`item-finish-${index}`} />
            </Field>
            <Field label="Tag / Card No.">
              <Input maxLength={150} value={item.tag_card_no} onChange={(e) => onChange("tag_card_no", e.target.value)} data-testid={`item-tag-no-${index}`} />
            </Field>
            <Field label="QR Link">
              <Input maxLength={150} value={item.qr_link} onChange={(e) => onChange("qr_link", e.target.value)} data-testid={`item-qr-${index}`} />
            </Field>
            <Field label="Chip No. / Encoding">
              <Input maxLength={150} value={item.chip_encoding} onChange={(e) => onChange("chip_encoding", e.target.value)} data-testid={`item-chip-${index}`} />
            </Field>
            <Field label="Variable Data">
              <Input maxLength={150} value={item.variable_data} onChange={(e) => onChange("variable_data", e.target.value)} data-testid={`item-var-${index}`} />
            </Field>
            <Field label="Thickness">
              <Input maxLength={150} value={item.thickness} onChange={(e) => onChange("thickness", e.target.value)} data-testid={`item-thickness-${index}`} />
            </Field>
          </div>
        </div>
      </div>

      {/* Image upload section */}
      <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[11px] uppercase tracking-widest text-slate-600 font-medium">Images &amp; Files</div>
          {item.item_name?.trim() && !isAlreadyInMaster && (
            <Button type="button" variant="outline" size="sm" onClick={saveToMaster} disabled={savingMaster}>
              {savingMaster ? "Saving…" : "Save as Product"}
            </Button>
          )}
        </div>

        {/* Front / Back dedicated slots — looked up by explicit slot tag, not array position */}
        <div className="grid grid-cols-2 gap-3">
          {[
            { slot: "front", label: "Front Side", ref: frontRef, file: pickFrontBack(item.artwork_files).front },
            { slot: "back",  label: "Back Side",  ref: backRef,  file: pickFrontBack(item.artwork_files).back },
          ].map(({ slot, label, ref, file }) => {
            const f = file;
            const url = f?.storage_path
              ? `${API_BASE.replace("/api", "")}/uploads/${f.storage_path}`
              : f?.id ? `${API_BASE}/files/${f.id}` : null;
            const isImg = f && (f.content_type || "").startsWith("image/");
            return (
              <div key={slot} className="border-2 border-dashed border-[#1d3d8f] rounded-lg overflow-hidden bg-white">
                <div className="bg-[#1d3d8f] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 text-center">{label}</div>
                <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e, slot)} />
                {f && url && isImg ? (
                  <div className="relative group">
                    <img src={url} alt={label} className="w-full object-contain max-h-36 bg-slate-50" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button type="button" onClick={() => ref.current?.click()} className="px-2 py-1 bg-white text-slate-800 text-xs rounded font-medium">Replace</button>
                      <button type="button" onClick={() => onRemoveFile(f.id)} className="px-2 py-1 bg-red-600 text-white text-xs rounded font-medium">Remove</button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => ref.current?.click()}
                    disabled={busy === slot}
                    className="w-full flex flex-col items-center justify-center gap-1 py-6 text-slate-400 hover:text-[#1d3d8f] hover:bg-blue-50 transition-colors"
                  >
                    <Upload className="w-5 h-5" />
                    <span className="text-[11px]">{busy === slot ? "Uploading…" : `Upload ${label}`}</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Other / additional reference images */}
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
            <div className="text-[11px] uppercase tracking-widest text-slate-600 font-medium">Other Reference Images</div>
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => handleFile(e, "other")} data-testid={`item-file-input-${index}`} />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy === "other"} data-testid={`item-upload-${index}`}>
                <Upload className="w-4 h-4 mr-1" /> {busy === "other" ? "Uploading…" : "Add files"}
              </Button>
            </div>
          </div>
          {pickFrontBack(item.artwork_files).others.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-4">No additional files. Select multiple files at once.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {pickFrontBack(item.artwork_files).others.map((f, fi) => {
                const url = f.storage_path ? `${API_BASE.replace("/api", "")}/uploads/${f.storage_path}` : `${API_BASE}/files/${f.id}`;
                return (
                  <div key={f.id} className="flex items-center gap-3 px-3 py-2">
                    <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{f.original_filename}</div>
                      <div className="text-xs text-slate-400">{(f.size / 1024).toFixed(1)} KB</div>
                    </div>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-600 hover:text-slate-900">View</a>
                    <Button type="button" variant="ghost" size="icon" onClick={() => onRemoveFile(f.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MaterialRow({ index, material, materialsMaster = [], onMaterialMasterSaved, onChange, onBatchChange, onRemove }) {
  const [pickerKey, setPickerKey] = useState(0);
  const [saving, setSaving] = useState(false);

  const isAlreadyInMaster = materialsMaster.some(
    (m) => m.name.trim().toLowerCase() === (material.material_name || "").trim().toLowerCase()
  );

  const pickFromMaster = (id) => {
    const mm = materialsMaster.find((x) => x.id === id);
    if (!mm) return;
    onBatchChange({ material_name: mm.name, unit: mm.unit || material.unit });
    setPickerKey((k) => k + 1);
  };

  const saveToMaster = async () => {
    if (!material.material_name?.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/materials-master/upsert", {
        name: material.material_name.trim(),
        unit: material.unit || "",
        description: "",
      });
      onMaterialMasterSaved(data);
      toast.success(`"${material.material_name}" saved to Materials`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-slate-700 text-white grid place-items-center text-[10px] font-mono">#{index + 1}</div>
          <div className="text-sm font-medium text-slate-800">Material {index + 1}</div>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onRemove}><Trash2 className="w-4 h-4 mr-1" /> Remove</Button>
      </div>

      <div className="mb-3">
        <Field label="Pick from saved Materials (auto-fills fields below)">
          <Select key={pickerKey} onValueChange={pickFromMaster}>
            <SelectTrigger className="bg-white">
              <SelectValue placeholder={materialsMaster.length ? "Select a saved material to auto-fill…" : "No materials saved yet"} />
            </SelectTrigger>
            <SelectContent>
              {materialsMaster.length === 0 && <SelectItem value="__none__" disabled>No materials yet — type below and save</SelectItem>}
              {materialsMaster.map((mm) => (
                <SelectItem key={mm.id} value={mm.id}>{mm.name}{mm.unit ? ` · ${mm.unit}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Material Name">
          <Input maxLength={150} value={material.material_name} onChange={(e) => onChange("material_name", e.target.value)} placeholder="e.g. Art Paper 90 GSM" data-testid={`mat-name-${index}`} />
        </Field>
        <Field label="Unit">
          <Input maxLength={150} value={material.unit} onChange={(e) => onChange("unit", e.target.value)} placeholder="e.g. kg, sheets" data-testid={`mat-unit-${index}`} />
        </Field>
        <Field label="Issued Qty">
          <Input type="number" min="0" value={material.issued_qty === 0 ? "" : material.issued_qty} onChange={(e) => onChange("issued_qty", Math.max(0, Number(e.target.value) || 0))} onFocus={(e) => { if (Number(e.target.value) === 0) e.target.value = ""; }} className="font-mono" data-testid={`mat-issued-${index}`} />
        </Field>
        <Field label="Consumed Qty">
          <Input type="number" min="0" value={material.consumed_qty === 0 ? "" : material.consumed_qty} onChange={(e) => onChange("consumed_qty", Math.max(0, Number(e.target.value) || 0))} onFocus={(e) => { if (Number(e.target.value) === 0) e.target.value = ""; }} className="font-mono" data-testid={`mat-cons-${index}`} />
        </Field>
        <Field label="Remarks" className="md:col-span-3">
          <Input maxLength={150} value={material.remarks} onChange={(e) => onChange("remarks", e.target.value)} />
        </Field>
        <Field label=" " className="flex items-end">
          {material.material_name?.trim() && !isAlreadyInMaster && (
            <Button type="button" variant="outline" size="sm" className="w-full" onClick={saveToMaster} disabled={saving}>
              {saving ? "Saving…" : "Save as Material"}
            </Button>
          )}
        </Field>
      </div>
    </div>
  );
}

function Section({ title, hint, children, actions }) {
  return (
    <Card className="border-slate-200 p-6 mb-4">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-yellow-600 font-semibold">{hint}</div>
          <div className="text-lg font-semibold" style={{ fontFamily: "var(--font-heading)" }}>{title}</div>
        </div>
        {actions}
      </div>
      {children}
    </Card>
  );
}

function Field({ label, children, required, className = "" }) {  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-[11px] uppercase tracking-widest text-slate-600 font-medium">
        {label} {required && <span className="text-yellow-600">*</span>}
      </Label>
      {children}
    </div>
  );
}

function ReconciliationSummary({ form }) {
  const printingTotal =
    (Number(form.good_qty) || 0) + (Number(form.rejection_qty) || 0) + (Number(form.wastage_qty) || 0);
  const productionTotal =
    (Number(form.production_good_qty) || 0) + (Number(form.production_rejection_qty) || 0) + (Number(form.production_wastage_qty) || 0);
  const ord = Number(form.ordered_qty) || 0;
  return (
    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
      <Stat label="Ordered" value={ord} />
      <Stat label="Printing Accounted" value={printingTotal} />
      <Stat label="Production Accounted" value={productionTotal} />
      <Stat label="Operator" value={form.operator_name || "—"} />
    </div>
  );
}
function Stat({ label, value, tone }) {
  const color = tone === "warn" ? "text-yellow-700" : tone === "ok" ? "text-emerald-700" : "text-slate-900";
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-lg font-mono mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}
